import type { InfiniteData } from "@tanstack/react-query";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient
} from "@tanstack/react-query";
import { instance } from "api/apiBase";
import { Layout } from "providers/Layout";
import { useEffect, useMemo, useState } from "react";
import { useInView } from "react-intersection-observer";
import { useSearchParamsState } from "shared/hooks/use-search-params-state";
import { LayoutHeader } from "./components/layout-header";
import { NotificationCard } from "./components/NotificationCard";
import { InboxNotificationCard } from "./components/InboxNotificationCard";
import { MissedCallNotificationCard } from "./components/MissedCallNotificationCard";
import { NotificationGrouper } from "./helpers/grouper";
import {
  MarkAllAsReadRequest,
  MarkAllAsReadResponse,
  Notification,
  NotificationKind,
  NotificationType
} from "./types";
import { HugeiconsIcon } from "@hugeicons/react";
import { Notification01Icon } from "@hugeicons-pro/core-stroke-rounded";
import { Card } from "shared/components/Card";
import { useAuthContext } from "providers/Auth";
import { toast } from "react-toastify";
import { getErrorMessage } from "utils/error";

const NOTIFICATIONS_PER_PAGE = 10;

type NotificationsPage = {
  items: Notification[];
  current_page: number;
  page_count: number;
  total_count: number;
  has_more: boolean;
};

const grouper = new NotificationGrouper({
  locale: "en-US",
  weekStartsOn: 1,
  keyOf: (it) => String(it.id ?? `${it.created_at}|${it.user?.id ?? ""}`)
});

const notificationApi = ({
  kind,
  unreadOnly,
  page
}: {
  kind: NotificationKind;
  unreadOnly: boolean;
  page: number;
}): Promise<NotificationsPage> => {
  return instance
    .get("/portal/v1/notifications", {
      params: {
        kind,
        unread_only: unreadOnly,
        page,
        per_page: NOTIFICATIONS_PER_PAGE
      }
    })
    .then((res) => res.data);
};

const unreadNotificationCountApi = ({ kind }: { kind: NotificationKind }) => {
  return instance
    .get("/portal/v1/notifications/unread_count?", {
      params: {
        kind
      }
    })
    .then((res) => res.data);
};

const markPagesAsRead = (
  cached: InfiniteData<NotificationsPage> | undefined,
  readAt: string
) =>
  cached
    ? {
        ...cached,
        pages: cached.pages.map((page) => ({
          ...page,
          items: page.items.map((item) =>
            item.read_at ? item : { ...item, read_at: readAt }
          )
        }))
      }
    : cached;

const markAllNotificationsAsReadApi = ({
  kind
}: MarkAllAsReadRequest): Promise<MarkAllAsReadResponse> => {
  return instance
    .post("/portal/v1/notifications/mark_all_as_read", { kind })
    .then((res) => res.data);
};

export const BoomNotificationView = () => {
  const { auth, type } = useAuthContext();
  const queryClient = useQueryClient();
  const [unreadOnly, setUnreadOnly] = useState<boolean>(false);
  const [searchParams, setSearchParams] = useSearchParamsState(
    {
      tab: NotificationKind.Mention
    },
    ["tab"]
  );

  // Seeing missed calls is visibility, not calling: the tab follows the same
  // gate as the /calls route so inbound-only users (no can_make_voice_calls)
  // still learn who tried to reach them. The "Call back" affordance inside the
  // card carries the stricter useVoiceEnabled() gate on its own.
  const showCallsTab =
    type.isCommunicationPhone && type.isProvisionedPhoneNumber;

  // Nothing validates the `tab` query param, so a deep-linked ?tab=call on a
  // partner without voice would leave BaseTabs highlighting a tab it isn't
  // rendering. Fall back to Mentions instead.
  const activeTab =
    searchParams.tab === NotificationKind.Call && !showCallsTab
      ? NotificationKind.Mention
      : (searchParams.tab as NotificationKind);

  const { data: unreadMentionCount } = useQuery<{
    total_unread: number;
  }>({
    queryKey: ["UNREAD_NOTIFICATIONS_COUNT", NotificationKind.Mention],
    queryFn: () =>
      unreadNotificationCountApi({
        kind: NotificationKind.Mention
      })
  });

  const { data: unreadAssignedCount } = useQuery<{
    total_unread: number;
  }>({
    queryKey: ["UNREAD_NOTIFICATIONS_COUNT", NotificationKind.Assigned],
    queryFn: () =>
      unreadNotificationCountApi({
        kind: NotificationKind.Assigned
      })
  });

  const { data: unreadMessageCount } = useQuery<{
    total_unread: number;
  }>({
    queryKey: ["UNREAD_NOTIFICATIONS_COUNT", NotificationKind.Message],
    queryFn: () =>
      unreadNotificationCountApi({
        kind: NotificationKind.Message
      })
  });

  const { data: unreadCallCount } = useQuery<{
    total_unread: number;
  }>({
    queryKey: ["UNREAD_NOTIFICATIONS_COUNT", NotificationKind.Call],
    queryFn: () =>
      unreadNotificationCountApi({
        kind: NotificationKind.Call
      }),
    enabled: showCallsTab
  });

  const notificationsQuery = useInfiniteQuery<NotificationsPage>({
    queryKey: ["NOTIFICATIONS", activeTab, unreadOnly],
    queryFn: ({ pageParam }) =>
      notificationApi({
        kind: activeTab,
        unreadOnly,
        page: pageParam as number
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.has_more ? lastPage.current_page + 1 : undefined
  });

  const notifications = useMemo(
    () => notificationsQuery.data?.pages.flatMap((p) => p.items) ?? [],
    [notificationsQuery.data]
  );

  const { ref: loadMoreRef, inView: loadMoreInView } = useInView({
    rootMargin: "200px 0px",
    threshold: 0
  });

  useEffect(() => {
    if (!loadMoreInView) return;
    if (!notificationsQuery.hasNextPage) return;
    if (notificationsQuery.isFetchingNextPage) return;
    notificationsQuery.fetchNextPage();
  }, [
    loadMoreInView,
    notificationsQuery.hasNextPage,
    notificationsQuery.isFetchingNextPage,
    notificationsQuery.fetchNextPage
  ]);
  const { data: unread_notification_count } = useQuery<{
    total_unread: number;
  }>({
    queryKey: ["UNREAD_NOTIFICATIONS_COUNT", activeTab],
    queryFn: () =>
      unreadNotificationCountApi({
        kind: activeTab
      }),
    enabled: Boolean(auth)
  });
  // Task/lead notifications the partner can't reach are hidden by the API, so the
  // list and every count here are already scoped.
  const unreadTotalCount = unread_notification_count?.total_unread || 0;

  const { mutate: markAllAsRead, isPending: isMarkingAllAsRead } = useMutation({
    mutationKey: ["NOTIFICATIONS", activeTab, "MARK_ALL_AS_READ"],
    mutationFn: (kind: MarkAllAsReadRequest["kind"]) =>
      markAllNotificationsAsReadApi({ kind }),
    onMutate: async (kind) => {
      await Promise.all([
        queryClient.cancelQueries({ queryKey: ["NOTIFICATIONS", kind] }),
        queryClient.cancelQueries({
          queryKey: ["UNREAD_NOTIFICATIONS_COUNT", kind]
        }),
        queryClient.cancelQueries({
          queryKey: ["TOTAL_UNREAD_NOTIFICATIONS"]
        })
      ]);

      const previousPages = queryClient.getQueriesData<
        InfiniteData<NotificationsPage>
      >({ queryKey: ["NOTIFICATIONS", kind] });
      const previousTabCount = queryClient.getQueryData<{
        total_unread: number;
      }>(["UNREAD_NOTIFICATIONS_COUNT", kind]);
      const previousTotalUnread = queryClient.getQueryData<number>([
        "TOTAL_UNREAD_NOTIFICATIONS"
      ]);

      const readAt = new Date().toISOString();
      queryClient.setQueriesData<InfiniteData<NotificationsPage>>(
        { queryKey: ["NOTIFICATIONS", kind] },
        (cached) => markPagesAsRead(cached, readAt)
      );
      queryClient.setQueryData(["UNREAD_NOTIFICATIONS_COUNT", kind], {
        total_unread: 0
      });
      queryClient.setQueryData<number>(
        ["TOTAL_UNREAD_NOTIFICATIONS"],
        (total) =>
          Math.max((total || 0) - (previousTabCount?.total_unread || 0), 0)
      );

      return { previousPages, previousTabCount, previousTotalUnread };
    },
    onError: (error, kind, context) => {
      context?.previousPages.forEach(([queryKey, data]) =>
        queryClient.setQueryData<InfiniteData<NotificationsPage> | undefined>(
          queryKey,
          data
        )
      );
      if (context?.previousTabCount)
        queryClient.setQueryData(
          ["UNREAD_NOTIFICATIONS_COUNT", kind],
          context.previousTabCount
        );
      if (context?.previousTotalUnread !== undefined)
        queryClient.setQueryData(
          ["TOTAL_UNREAD_NOTIFICATIONS"],
          context.previousTotalUnread
        );
      toast.error(getErrorMessage(error));
    },
    onSuccess: (data) => {
      queryClient.setQueryData(
        ["TOTAL_UNREAD_NOTIFICATIONS"],
        data.total_unread
      );
    },
    onSettled: (_data, _error, kind) => {
      queryClient.invalidateQueries({ queryKey: ["NOTIFICATIONS", kind] });
      queryClient.invalidateQueries({
        queryKey: ["UNREAD_NOTIFICATIONS_COUNT", kind]
      });
      queryClient.invalidateQueries({
        queryKey: ["TOTAL_UNREAD_NOTIFICATIONS"]
      });
      queryClient.invalidateQueries({ queryKey: ["NEWEST_NOTIFICATIONS"] });
    }
  });
  const handleMarkAllAsRead = () => markAllAsRead(activeTab);

  const groupedNotifications = useMemo(
    () => grouper.group(notifications),
    [notifications]
  );
  return (
    <Layout
      headerClassName="border-b border-alpha-100"
      header={
        <LayoutHeader
          unreadTotalCount={unreadTotalCount}
          tab={activeTab}
          onTabChange={(tab) => setSearchParams({ ...searchParams, tab })}
          unreadOnly={unreadOnly}
          toggleUnreadOnly={() => setUnreadOnly((prev) => !prev)}
          unreadMentionCount={unreadMentionCount?.total_unread || 0}
          unreadAssignedCount={unreadAssignedCount?.total_unread || 0}
          unreadMessageCount={unreadMessageCount?.total_unread || 0}
          unreadCallCount={unreadCallCount?.total_unread || 0}
          showCallsTab={showCallsTab}
          isMarkingAllAsRead={isMarkingAllAsRead}
          onMarkAllAsRead={handleMarkAllAsRead}
        />
      }
    >
      <div className="mt-6 space-y-6">
        {groupedNotifications.length > 0 ? (
          <div className="mt-6 space-y-6">
            {groupedNotifications.map((group) => (
              <div key={group.name}>
                <h2 className="text-base font-medium mb-4 text-content-400">
                  {group.name}
                </h2>
                <Card className="overflow-hidden divide-y divide-content-80">
                  {group.items.map((item) => {
                    if (
                      item.type === NotificationType.InboxMessage ||
                      item.type === NotificationType.InboxMessageDigest
                    )
                      return (
                        <InboxNotificationCard key={item.id} item={item} />
                      );

                    if (item.type === NotificationType.MissedCall)
                      return (
                        <MissedCallNotificationCard key={item.id} item={item} />
                      );

                    return (
                      <NotificationCard
                        key={item.id}
                        id={item.id}
                        kind={item.kind}
                        recipientName={item.user.name}
                        authorName={
                          typeof item.author === "string"
                            ? item.author
                            : item.author?.name || ""
                        }
                        readAt={item.read_at}
                        richText={item.rich_text}
                        type={item.type}
                        url={item.url}
                        metadata={item.metadata}
                        createAt={item.created_at}
                      />
                    );
                  })}
                </Card>
              </div>
            ))}
            <div ref={loadMoreRef} className="h-1 w-full" />
          </div>
        ) : (
          <div className="mt-10 flex flex-auto flex-col items-center justify-center">
            <div className="mb-6 flex h-[64px] w-[64px] items-center justify-center rounded-full bg-alpha-40 p-5">
              <HugeiconsIcon
                icon={Notification01Icon}
                size={24}
                className="text-content-500"
              />
            </div>
            <p className="text-sm font-medium text-content-300">
              No notifications yet
            </p>
          </div>
        )}
      </div>
    </Layout>
  );
};
